import React, { useRef, useEffect, useState, useCallback } from 'react';
import Icon from '../shared/Icon';
import Chip from '../shared/Chip';
import { WIDGET_TEXT_EDIT_FLAG } from './WidgetTextEditModal';
import './WidgetCard.css';
import { getRenderer, rendererInput, isCompositeWidgetType } from './renderers';
import SummaryRow from './SummaryRow';
import { useWidgetQuery } from './useWidgetQuery';
import { isWidgetStale } from './refreshPolicy';
import { rangeLabel } from './insightsRange';
import { useFlag } from '@unleash/proxy-client-react';
import { apiService } from '../../../services/api';
import TabStateIcon from './TabStateIcon';
import Timestamp from '../shared/Timestamp';
import { toCsvRowsAsync, downloadCsv, buildCsvFilename } from '../../../utils/csv';

// A strip is only as current as its stalest tile, so that is the time the header
// claims. Taking the newest would date the whole row by whichever tile happened to
// finish last, which is the opposite of what a reader needs to know.
function oldestTileStamp(data) {
  const stamps = (Array.isArray(data?.tiles) ? data.tiles : [])
    .map((t) => t?.computed_at)
    .filter(Boolean)
    .sort();
  return stamps[0] || null;
}

function WidgetCard({
  widget, onRemove, onEdit, onEditText, onShare, onRefreshed, onDrilldown, onShowNotification, datasetName,
  moveTargets, onMoveToTab,
}) {
  const chartRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [menuOpen, setMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const menuRef = useRef(null);
  const Renderer = getRenderer(widget.widget_type_id);
  // A stored type this build has no renderer for (e.g. saved by a newer Console, or
  // a family whose flag has since gone off). Say so rather than draw the wrong chart,
  // and don't spend an agent call on a result nothing can show.
  const unsupported = !Renderer;
  // A composite type assembles its result from other types, one query per tile, so
  // the card runs no query of its own and none of the single-result failure taxonomy
  // below applies to it. It delegates the whole chart area instead.
  const isComposite = isCompositeWidgetType(widget.widget_type_id) && !unsupported;
  const [tileRefreshKey, setTileRefreshKey] = useState(0);

  // PRD-1336 R7: only treat a widget as "shared to me" when org-widget-sharing is ON.
  // With the flag OFF the backend serves every widget in the org (legacy) and
  // is_shared just means "not mine" — badging a colleague's PRIVATE widget "Shared"
  // (and hiding its menu) would be wrong (Cody #3). Gate both on the flag so they
  // can't appear while the backend is in legacy mode.
  const sharingEnabled = useFlag('org-widget-sharing');
  const isSharedView = sharingEnabled && widget.is_shared;

  // PRD-1273 live re-query (re-run the saved question on load + manual refresh,
  // as the viewer) is gated so it can be rolled out safely. Flag OFF = the prior
  // behavior: render the value stored when the widget was created, no auto-query,
  // no refresh button.
  const liveRefreshEnabled = useFlag('enable_insights_widget_refresh');
  // PRD-1578: label-instead-of-title on KPI cards, and the text-only Edit.
  const textEditEnabled = useFlag(WIDGET_TEXT_EDIT_FLAG);
  const editText = textEditEnabled ? onEditText : undefined;

  // PRD-1273 §6: a saved widget is a saved question, not a saved answer. Re-query
  // on load and on manual refresh so the card shows current data (and runs as the
  // viewer, so permissions are re-checked each render), rather than the value
  // stored when it was created.
  const { status, data: liveData, error, errorType, details, metadata, executeQuery, reset } = useWidgetQuery();
  const [fetchedAt, setFetchedAt] = useState(null);

  const runQuery = useCallback(() => {
    // Refresh on a composite means "re-run every tile", which the tiles do by
    // watching this key. The card has no single query to re-run.
    if (isComposite) {
      setTileRefreshKey((k) => k + 1);
      return;
    }
    if (!widget.prompt) return;
    // PRD-1370 #15: a saved widget refreshes deterministically by re-running the SQL
    // resapi stored on it. The client sends only the widget_id (and the column mapping,
    // a rendering hint) — never the SQL or the tables. The server loads the stored SQL
    // and resolves the permitted tables itself, so the client cannot choose the query
    // or its read scope (security review on #466). First preview has no id yet.
    const snap = widget.data || {};
    const opts = {
      widget_id: widget.id,
      label_field: snap.label_field,
      value_field: snap.value_field,
      series_field: snap.series_field,
      bin_width: snap.bin_width,
    };
    executeQuery(widget.widget_type_id, widget.prompt, widget.dataset_ids || [], widget.time_range, opts);
  }, [
    // isComposite belongs here as well as in main's list: the early return above reads
    // it, so a widget that becomes (or stops being) a strip has to re-make this callback.
    isComposite,
    widget.id,
    widget.prompt,
    widget.widget_type_id,
    widget.dataset_ids,
    widget.time_range,
    widget.data,
    executeQuery,
  ]);

  // Auto-query ONCE per widget config (on load, and again only if the config
  // actually changes) — not on every parent re-render. `dataset_ids` is an array
  // whose reference churns across renders, so key off a stable serialization,
  // not the array identity; otherwise App's periodic re-renders re-fire the query
  // every ~15s and it looks like an auto-refresh (there is no auto-refresh cadence
  // — refresh is load + the manual button only).
  const loadKey = `${widget.id}|${widget.widget_type_id}|${widget.time_range || ''}|${widget.prompt || ''}|${JSON.stringify(
    widget.dataset_ids || [],
  )}`;
  const lastLoadKey = useRef(null);
  useEffect(() => {
    if (!liveRefreshEnabled || unsupported || isComposite) return;
    if (lastLoadKey.current === loadKey) return;
    lastLoadKey.current = loadKey;
    // PRD-1336 R4: don't re-query on load when the cached result is still valid
    // under the widget's refresh policy — serve the stored result instead. Query
    // only when there's no cached result or it has gone stale. (Manual refresh
    // via the button always runs, regardless of policy.)
    // A widget whose dataset the read already reported as deleted has nothing to
    // ask for: the query can only come back with the state the card is showing,
    // and on a stale one that costs a round trip (and an agent call, if it falls
    // through to generation) to be told what we knew before rendering.
    if (widget.dataset_deleted) return;
    // Same reasoning for a dataset this viewer may not read: the query can only
    // come back 403, and rendering that as an error is what made a deliberately
    // shared widget look broken to everyone it was shared with.
    if (widget.dataset_readable === false) return;
    if (!widget.data || isWidgetStale(widget)) {
      runQuery();
    }
    // runQuery is intentionally omitted — loadKey captures every input it uses.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKey, liveRefreshEnabled, unsupported, isComposite]);

  // An edit replaces the widget's stored result, and the card renders
  // `liveData || widget.data` — so a live result from BEFORE the edit outranks the
  // one the edit just saved, and the card goes on showing the old answer until
  // someone presses Refresh. The re-query does not rescue it either: the freshly
  // saved result is not stale, so the load effect deliberately skips.
  //
  // Keyed on `last_computed_at`, which the server stamps whenever an edit or a
  // refresh writes new data. Not on `widget.data` identity: that object is replaced
  // on every fetch of the list, which would throw away a good live result on any
  // unrelated re-render.
  // Set just before this card reports its own result, because that report is what
  // moves `last_computed_at` — so without it the effect below cannot tell an EDIT
  // from this card's own refresh, and resets after every one. That clears
  // `metadata`, and with it the truncation overlay, one render after a capped chart
  // earns it: "a capped chart mustn't read as complete" is the rule that would
  // quietly stop holding.
  const selfWrite = useRef(false);
  const lastSeenComputedAt = useRef(widget.last_computed_at);
  useEffect(() => {
    if (lastSeenComputedAt.current === widget.last_computed_at) return;
    lastSeenComputedAt.current = widget.last_computed_at;
    if (selfWrite.current) {
      selfWrite.current = false;
      return;
    }
    // Mid-flight results belong to the new config anyway; only a settled older one
    // needs clearing.
    if (status !== 'loading') reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget.last_computed_at]);

  // PRD-1336 R4: on a successful (background or manual) refresh, stamp the fetch
  // time and persist the fresh result so the cache is durable across page loads
  // (the next visit within the policy window serves it without a query).
  useEffect(() => {
    if (status === 'success' && liveData) {
      setFetchedAt(new Date().toISOString());
      // Armed only when there is actually a listener, because the listener is what
      // moves `last_computed_at`. Arming unconditionally left the latch set after a
      // card with no listener rendered, and the next genuine edit was swallowed by
      // it — the stale result stayed on screen, which is the bug this was fixing.
      if (onRefreshed) {
        selfWrite.current = true;
        onRefreshed(widget.id, liveData, metadata);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Show fresh data once it arrives; keep the stored value visible until then so a
  // refresh never blanks the card (R5). A failed refresh keeps the last good value.
  const chartData = liveRefreshEnabled ? (liveData || widget.data) : widget.data;
  const hasData = !!chartData;
  const isRefreshing = liveRefreshEnabled && status === 'loading';

  // Four things can go wrong and they need four different messages. The backend
  // now states which one happened rather than leaving it to be guessed from the
  // wording of the agent's sentence.
  const isError = liveRefreshEnabled && status === 'error';
  const isEmpty = isError && errorType === 'empty_result';
  const isDisabled = isError && errorType === 'feature_disabled';
  const isDenied = isError && errorType === 'permission_denied';
  // Distinct from `isDenied` on purpose. "You don't have access" is the wrong
  // sentence for a dataset the author deleted themselves: there is no permission to
  // restore, and the card used to offer no way out of a state it would stay in
  // forever. This one names what happened and carries the two things that fix it.
  // Either the query said so, or the read did. The read matters because a card
  // only re-queries when its stored result has gone stale: without the flag, a
  // widget whose dataset was deleted an hour ago renders its last good result and
  // looks perfectly healthy, while being permanently dead. The same pattern
  // automations already use (`dataset_deleted` on their list endpoint).
  const isDatasetGone = (isError && errorType === 'dataset_unavailable') || !!widget.dataset_deleted;
  // Shared with you, but the dataset behind it was not. The card shows the result
  // the list response already carried and withholds every control that reaches back
  // to the data. Strict `=== false`: an older server omits the field, and treating
  // absent as unreadable would blank working cards. Absent means the card queries as
  // before and the server still refuses -- degraded, not unsafe.
  const isDatasetUnreadable = widget.dataset_readable === false;
  // Seen only through a shared tab, on a dataset this viewer may not read: the server
  // sent no result at all, so there is nothing "last saved" to show.
  const isDataWithheld = isDatasetUnreadable && widget.data_withheld === true;
  // Named for what it is: the ANSWER was cut short by the model's output limit.
  // Distinct from `truncated` below, which means the chart was capped for display.
  const isAnswerCutShort = isError && errorType === 'truncated';
  // A hard error is everything left: network, agent, a malformed result. It only
  // takes over the card when there's no stored/live value to keep showing —
  // otherwise we keep the last good chart and flag the refresh failure inline (R5).
  const hardError = isError && !isEmpty && !isDisabled && !isDenied && !isDatasetGone && !isAnswerCutShort
    && errorType !== 'not_single_value';
  const showErrorPanel = hardError && !hasData;
  const refreshFailed = hardError && hasData;
  // A single-value widget whose question turned out to have a table or a series
  // for an answer. The query worked; the widget is asking the wrong kind of
  // question. Re-running gives the same answer, so the route offered is Edit.
  const isNotSingleValue = isError && errorType === 'not_single_value';
  // A result the widget's own setup rules out — wrong shape for the type, too few
  // points for a line chart. Re-running returns the same answer; the remedy is to
  // edit the widget, so these get the message without a Retry.
  const isNotRetryable =
    hardError && ['validation_failed', 'type_mismatch', 'series_not_drawable'].includes(errorType);
  // A cut-short answer only takes over the card when there is nothing else to show.
  // The hook drops the payload of an error result, and the agent's truncation
  // fallback carries no chart anyway, so whatever stays on screen is the LAST
  // COMPLETE result — not this turn's partial one — and the caption says so.
  const cutShortNoData = isAnswerCutShort && !hasData;

  // Truncation must be visible, never silent — a capped chart mustn't read as
  // complete (PR #165 review §2). Only trust live metadata (stored data has none).
  const liveMeta = liveRefreshEnabled ? metadata : null;
  // The overlay note is a chart concern (points). A table says its own row count
  // in its caption, and its metadata carries rows, not points.
  const truncated = !!liveMeta?.truncated && !isError && liveMeta.shown_points != null;
  // The refresh came back cut short while a stored chart is still showing.
  const cutShortWithData = isAnswerCutShort && hasData;

  // PRD-1337: only offer "Ask a follow-up" when an actual chart is on screen. hasData is
  // true whenever a stored blob exists, but a denied / empty / disabled / unsupported /
  // cut-short-no-data card renders a state panel, not a chart — offering to chat about a
  // "You don't have access" panel just earns a second 403 (review on #182).
  const chartOnScreen =
    hasData && !unsupported && !isDenied && !isDatasetGone && !isEmpty && !isDisabled
    && !showErrorPanel && !cutShortNoData;

  // PRD-1336 R4: surface when the RESULT was computed — the live fetch time after a
  // refresh, otherwise the persisted last_computed_at (falling back to write time).
  const stamp = isComposite
    ? (oldestTileStamp(widget.data) || widget.last_computed_at || widget.updated_at || widget.created_at)
    : (liveRefreshEnabled && fetchedAt) || widget.last_computed_at || widget.updated_at || widget.created_at;

  // A failure has to leave the card. Someone scrolled past a widget, or looking at
  // a different page, never learns their dashboard stopped working otherwise.
  // Quiet cases: not a failure at all (empty result, disabled family), or a failure
  // the card already flags inline beside a chart that is still on screen (a refresh
  // that failed, or came back cut short). A denial is loud even with a stored
  // chart, because that chart is being taken away.
  const notifiedKey = useRef(null);
  useEffect(() => {
    // Only a SETTLED state moves the key. 'loading' deliberately does not: every
    // manual Refresh passes through it, so re-arming there would re-announce the
    // same failure on each retry.
    if (status !== 'success' && status !== 'error') return;
    // The key tracks the last settled state, not the last ANNOUNCED one, and it is
    // updated before the announce guard below. That is what lets a denial re-announce
    // after a quiet failure in between: the sequence denied -> agent_error -> denied
    // moves the key each time, so the third differs from the second and speaks.
    // Keying only on what was announced left the stale 'denied' key in place through
    // the quiet middle state and swallowed the second denial.
    const key = status === 'success' ? null : `${widget.id}|${errorType || 'error'}`;
    const isRepeat = key !== null && notifiedKey.current === key;
    notifiedKey.current = key;
    if (isRepeat) return;

    // Which failures stay quiet: the card already says these inline, beside a result
    // that is still on screen.
    if (!isError || isEmpty || isDisabled || cutShortWithData) return;
    // A refresh failure over a stored chart is normally quiet for the same reason --
    // the last good result is still there and the card flags it inline. The exception
    // is a failure re-running cannot fix: the card removes Retry for those, so a quiet
    // "couldn't refresh" points at a button that is not there and names no way out.
    if (refreshFailed && !isNotRetryable) return;

    const title = widget.title || 'Widget';
    // Reported in three parts, because one failure and twenty need different things.
    //
    // `reason` is short and carries no title, so a batch where everything failed the
    // same way can state it once instead of repeating it per widget — which is the
    // ordinary case, since an outage or an expired token takes the page down at once.
    // `full` carries the backend's own sentence for the non-retryable cases, because
    // it names the remedy ("Edit the widget to ask for a count") where a generic
    // summary cannot. Safe to show since PRD-1391: every message is reviewed copy or
    // sanctioned agent prose, so it can no longer be a Python type name or an
    // upstream error.
    const reason = isDatasetGone
      ? 'its dataset is no longer available'
      : isDenied
      ? "you don't have access to this data"
      : isAnswerCutShort
        ? 'answer was cut short'
        : isNotSingleValue
          ? 'not a single value — edit the widget'
          : isNotRetryable
            ? "can't be shown — edit the widget"
            : "couldn't load";
    const full = isNotRetryable && error ? `${title}: ${error}` : `${title}: ${reason}`;
    onShowNotification?.(title, reason, full);
  }, [
    status, isError, isEmpty, isDisabled, isDenied, isDatasetGone, isAnswerCutShort, isNotSingleValue,
    isNotRetryable,
    refreshFailed, cutShortWithData, errorType, error, widget.id, widget.title, onShowNotification,
  ]);

  // Watch the chart container's actual rendered size — the parent grid
  // dictates dimensions now (react-grid-layout owns drag/resize), and we
  // just need to redraw the chart whenever the cell size changes.
  useEffect(() => {
    if (!chartRef.current) return;
    const measure = () => {
      if (chartRef.current) {
        const rect = chartRef.current.getBoundingClientRect();
        setSize({ width: rect.width, height: rect.height });
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(chartRef.current);
    return () => observer.disconnect();
  }, []);

  // A table card shows at most a capped page and says "100 of 55,753". This is how
  // someone gets the 55,753: the server re-runs the widget's own stored query with a
  // much higher ceiling and returns every row, which we turn into a file here.
  //
  // Offered only for a table with a stored query. `has_stored_query` is a boolean the
  // read hands us -- the SQL itself stays server-side -- so a widget saved before the
  // query was kept there shows no option rather than an option that can only fail.
  // Export re-runs the stored query server-side, so it needs dataset access too.
  const canExportCsv =
    widget.widget_type_id === 'table' && !!widget.has_stored_query && !isDatasetUnreadable;

  const handleDownloadCsv = useCallback(async () => {
    if (!canExportCsv || exporting) return;
    setExporting(true);
    try {
      const result = await apiService.exportWidgetRows(widget.id);
      const columns = result?.columns || [];
      const rows = result?.rows || [];
      // Kept positional end to end. Keying these rows by column name would drop a
      // column whenever two share a title, which display titles can.
      const csv = await toCsvRowsAsync(columns, rows);
      downloadCsv(csv, buildCsvFilename(widget.title));
      if (result?.truncated) {
        // Said out loud rather than swallowed: a file that is missing rows must not
        // present itself as the whole answer.
        onShowNotification?.(
          `Downloaded the first ${rows.length.toLocaleString()} of ${(result.total_rows || 0).toLocaleString()} rows.`,
          'info',
        );
      }
    } catch (err) {
      const message = err?.status === 410
        ? 'The dataset behind this widget is no longer available.'
        : err?.status === 409
        ? 'Refresh this widget once, then download.'
        : err?.status === 403
          ? 'You no longer have access to the data behind this widget.'
          : (err?.message || 'The download could not be produced.');
      onShowNotification?.(message, 'error');
    } finally {
      setExporting(false);
      setMenuOpen(false);
    }
  }, [canExportCsv, exporting, widget.id, widget.title, onShowNotification]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  return (
    <div className="wc-card">
      <div className="wc-header">
        <div className="wc-header-info">
          <h3 className="wc-title">
            {/* PRD-1578: a KPI shows no title while its value is drawn -- the label under
                the value names it -- but the heading keeps the name for screen readers.
                Any state panel (error, dataset gone, not enabled…) shows it, or a broken
                KPI would be a card nobody can identify. */}
            {textEditEnabled && widget.widget_type_id === 'kpi' && chartOnScreen
              ? <span className="wc-sr-only">{widget.title}</span>
              : widget.title}
            {/* PRD-1336 R7: a widget shown to you via org-sharing (not yours). */}
            {isSharedView && <Chip className="wc-shared-badge">Shared</Chip>}
            {/* Says why the card is inert, next to the reason it is visible at all.
                Without it the controls are simply missing and the viewer is left to
                guess whether the widget is broken. */}
            {isDatasetUnreadable && (
              <Chip
                className="wc-shared-badge"
                title={isDataWithheld
                  ? "This widget is on a tab shared with you, but you don't have access to its dataset, so its results aren't shown."
                  : "You can see this widget because it was shared with you, but you don't have access to its dataset. It shows the last saved result and can't be refreshed or asked about."}
              >
                View only
              </Chip>
            )}
          </h3>
          <span className="wc-meta">
            {/* The stamp is only honest when a result is on screen. Only a success
                sets fetchedAt, so over any failure panel it would be stale — and a
                clean "Last fetched" above an error contradicts the card. */}
            {unsupported
              ? 'Not supported'
              // No result was sent, so there was no fetch to date.
              : isDataWithheld
              ? 'Not shared with you'
              : isRefreshing
              ? 'Refreshing…'
              : refreshFailed
                // A non-retryable failure is not a "refresh" problem: re-running is
                // exactly what cannot fix it, and the card removes the Retry button.
                // Say what the remedy is instead of naming the one that is absent.
                ? (isNotRetryable ? (error || 'Can’t be shown — edit the widget') : 'Couldn’t refresh — showing last result')
                : cutShortWithData
                  ? 'Answer cut short — showing last complete result'
                  : isDatasetGone
                    ? 'Dataset unavailable'
                    : isDenied
                    ? 'Not available'
                    : isNotSingleValue
                      ? 'Not a single value'
                      : cutShortNoData
                      ? 'Answer cut short'
                      : showErrorPanel
                        ? 'Couldn’t load'
                        : isEmpty
                          ? 'No results'
                          : isDisabled
                            ? 'Not enabled'
                            : <>Last fetched: <Timestamp value={stamp} /></>}
          </span>
          {/* PRD-1370 #2: metadata (the time range) lives in the header now; the
              footer provenance block was removed and the dataset name dropped (#3).

              A composite widget has no single range to vouch for: each tile carries its
              own, and a platform tile has none at all. Naming one here would be a
              provenance claim the strip does not support -- so it says where the ranges
              actually live. This condition moved up from the footer it used to sit in. */}
          {/* PRD-1467: a composite shows each tile's own range beneath the tile (see
              SummaryTile / SummaryRowRenderer); the old "Range per tile" placeholder
              named no range at all, so drop it here for composites. */}
          {!isComposite && (
            <span className="wc-meta wc-range">
              {rangeLabel(widget.time_range, liveMeta?.resolved_time_range || widget.data?.resolved_time_range)}
            </span>
          )}
          {/* Set only on a multi-dataset widget: which datasets, and joined or side by side. */}
          {widget.data?.combination && <span className="wc-meta">{widget.data.combination}</span>}
        </div>
        {/* The header Refresh is a general "re-run this widget" control and stays
            available in every failure state — including a denial, where it is the only
            way to re-run after access is granted. What each failure panel withholds is
            its own Retry, which reads as a promise that re-running will fix it. The one
            exception is a type this build cannot draw: there is nothing to re-run into. */}
        {liveRefreshEnabled && !unsupported && !isDatasetUnreadable && (
          <button
            className="wc-refresh-btn"
            onClick={runQuery}
            disabled={isRefreshing}
            title="Refresh"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Icon name="refresh" />
          </button>
        )}
        {/* PRD-1337: open a drill-down conversation about this widget in Ask ResAI.
            A dedicated action (not a chart click) so it stays clear of future
            element-level drill-down, and it's visible on shared widgets too. */}
        {/* Drill-down runs NEW queries against the dataset, so it needs the grant the
            card itself does not. Hidden rather than disabled: the server refuses it
            anyway, and a visible control that always fails is worse than none. */}
        {onDrilldown && chartOnScreen && !isDatasetUnreadable && (
          <button
            type="button"
            className="wc-drilldown-btn"
            onClick={() => onDrilldown(widget)}
            title="Ask a follow-up"
            aria-label="Ask a follow-up"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Icon name="forum" />
          </button>
        )}
        {/* PRD-1336 R7: edit/delete are owner-only. A widget you only see because
            it is org-shared has no menu (the server rejects the mutations anyway). */}
        {!isSharedView && (
        <div className="wc-menu-wrap" ref={menuRef}>
          <button
            className="wc-menu-btn"
            onClick={() => setMenuOpen(prev => !prev)}
            title="More"
            // The grid uses .wc-header as its drag handle; stop propagation
            // so clicking the menu button doesn't initiate a drag.
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Icon name="more_vert" />
          </button>
          {menuOpen && (
            // Stop mousedown bubbling so react-grid-layout's drag handler
            // on .wc-header doesn't suppress the click on these items.
            <div className="wc-dropdown" onMouseDown={(e) => e.stopPropagation()}>
              {/* Nothing to edit into for a type this build can't draw; Delete stays. */}
              {/* PRD-1578: Edit changes the words only and runs nothing; Edit prompt is
                  the full wizard, which asks the question again. */}
              {editText && !unsupported && (
                <button onClick={() => {
                  setMenuOpen(false);
                  editText(widget);
                }}>
                  Edit
                </button>
              )}
              {onEdit && !unsupported && (
                <button onClick={() => {
                  setMenuOpen(false);
                  onEdit(widget);
                }}>
                  {editText ? 'Edit prompt' : 'Edit'}
                </button>
              )}
              {/* PRD-1370 #8/#9: Share is a first-class action (was missing entirely).
                  It flips the widget's org visibility via the owner-only update path.
                  Gated on the same org-widget-sharing flag as the creator modal, so a
                  tenant without sharing never sees a Share it can't use (review on #188). */}
              {onShare && sharingEnabled && (
                <button onClick={() => {
                  setMenuOpen(false);
                  onShare(widget);
                }}>
                  {widget.visibility === 'organization' ? 'Unshare' : 'Share'}
                </button>
              )}
              {/* Dashboard tabs: the owner's other tabs, each with its state icon so
                  moving onto a shared tab is visibly a share. Only offered for the
                  owner's own widgets; the parent passes no targets otherwise. */}
              {onMoveToTab && moveTargets?.length > 0 && (
                <div className="wc-dropdown-group" role="group" aria-label="Move to tab">
                  <span className="wc-dropdown-label">Move to</span>
                  {moveTargets.map((tab) => (
                    <button
                      key={tab.id}
                      className="wc-dropdown-move"
                      onClick={() => {
                        setMenuOpen(false);
                        onMoveToTab(widget, tab.id);
                      }}
                    >
                      <TabStateIcon state={tab.state} />
                      <span>{tab.name}</span>
                    </button>
                  ))}
                </div>
              )}
              {/* The menu stays open while this runs. A 50,000-row export is not
                  instant, and closing immediately would leave the click with no
                  visible effect at all until the file lands. */}
              {canExportCsv && (
                <button onClick={handleDownloadCsv} disabled={exporting}>
                  {exporting ? 'Preparing CSV…' : 'Download CSV'}
                </button>
              )}
              {onRemove && (
                <button
                  className="wc-dropdown-danger"
                  onClick={() => {
                    setMenuOpen(false);
                    onRemove(widget.id);
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
        )}
      </div>
      <div className="wc-chart" ref={chartRef}>
        {unsupported ? (
          <div className="wc-state wc-state-unsupported">
            <Icon name="extension_off" />
            <p>This widget type isn't supported in this version.</p>
          </div>
        ) : isDatasetGone ? (
          // Two actions, because they are the only two that help: point the widget
          // at a dataset that exists, or remove it. No Retry — the dataset will not
          // come back, and offering one would be the dead end this replaces.
          //
          // Moved ABOVE the composite branch: a summary strip on a deleted dataset
          // was reaching SummaryRow and firing one query per tile, collecting the
          // 410s this state exists to replace. Raised in review of #210.
          <div className="wc-state wc-state-dataset-gone">
            <Icon name="link_off" />
            <p>{error || 'The dataset behind this widget is no longer available.'}</p>
            <div className="wc-state-actions">
              {onEdit && !isSharedView && (
                <button className="wc-retry-btn" onClick={() => onEdit(widget)}>Reconfigure</button>
              )}
              {onRemove && !isSharedView && (
                <button className="wc-retry-btn" onClick={() => onRemove(widget.id)}>Remove</button>
              )}
            </div>
          </div>
        ) : isDatasetUnreadable && (!hasData || isComposite) ? (
          // Nothing to draw, or nothing that can be drawn without going back to the
          // data. A widget shared before it was ever computed has no saved result,
          // and a summary strip fetches per TILE rather than rendering `widget.data`
          // -- so letting it through would fire one query per tile and collect the
          // 403s this change exists to avoid.
          //
          // Checked ABOVE the composite branch on purpose. The comment there says no
          // failure branch can apply because the card ran no query, which is true of
          // every branch that reports a query's outcome -- and false of this one and
          // the gone state, which are known from the READ before any query runs.
          // Raised in review of #210.
          <div className="wc-state">
            <Icon name="visibility_off" />
            <p>
              {isDataWithheld
                ? "You don't have access to this widget's data."
                : isComposite
                  ? "This widget's data isn't shared with you, so its tiles can't be loaded."
                  : "No saved result to show, and this widget's dataset isn't shared with you."}
            </p>
          </div>
        ) : isComposite ? (
          // PRD-1467: serve the stored strip without re-querying when it's still fresh.
          // The live SummaryRow re-runs every tile's agent query on mount, so a reload
          // of an up-to-date widget dropped all tiles to loading dots for 15s+ behind
          // the per-tile concurrency gate. Only go live when there's no stored result,
          // the result is stale under the refresh policy, or the user hit Refresh
          // (tileRefreshKey bumps only on the manual button for composites).
          (Array.isArray(widget.data?.tiles) && widget.data.tiles.length > 0
            && !isWidgetStale(widget) && tileRefreshKey === 0) ? (
            <Renderer data={widget.data} width={size.width} title={widget.title} />
          ) : (
            // The strip owns its own queries, one per tile, and reports the assembled
            // result back once. Placed above the failure branches below because none of
            // them can apply: the card ran no query, so there is no single status to
            // have failed. A tile that fails says so in its own cell.
            <SummaryRow
              widget={widget}
              width={size.width}
              refreshKey={tileRefreshKey}
              onRefreshed={(assembled) => {
                setFetchedAt(new Date().toISOString());
                onRefreshed?.(widget.id, assembled, null);
              }}
            />
          )
        ) : showErrorPanel ? (
          <div className="wc-state wc-state-error">
            <Icon name="error_outline" />
            <p>{error}</p>
            {!isNotRetryable && <button className="wc-retry-btn" onClick={runQuery}>Retry</button>}
          </div>
        ) : isEmpty && Array.isArray(details?.columns) && details.columns.length > 0 ? (
          // A table that matched nothing still has columns: show them, so the
          // reader sees what the question was asking for, with the message in the body.
          // Rendered by the widget's own renderer, so the card names no type:
          // only the tabular path attaches columns to an empty result, and
          // `unsupported` above guarantees Renderer is resolved here.
          <Renderer
            data={{ columns: details.columns, column_types: details.column_types || [], rows: [], total_rows: 0 }}
            title={widget.title}
            emptyMessage={error || 'No matching records.'}
          />
        ) : isEmpty ? (
          // A query that ran and matched nothing is a valid result, not a failure.
          <div className="wc-state">
            <Icon name="search_off" />
            <p>{error || 'No matching records.'}</p>
          </div>
        ) : isDenied ? (
          // Not a fault the user can retry past, so no Retry button — the message
          // carries the route to access instead.
          <div className="wc-state wc-state-denied">
            <Icon name="lock" />
            <p>{error || "You don't have access to this data."}</p>
          </div>
        ) : isNotSingleValue ? (
          // Takes the card even over a stored number. Unlike a failed refresh, the
          // last value is not "the last complete result" of this question — the
          // question has a different kind of answer now, and showing the old number
          // would be a stale figure nobody should trust. The backend message names
          // the shape that came back and suggests Table or Chart; Edit is the route.
          <div className="wc-state wc-state-not-single">
            <Icon name="view_list" />
            <p title={error || undefined}>{error || 'This answer is not a single value. Try a Table or Chart widget instead.'}</p>
            {onEdit && !isSharedView && (
              <button className="wc-retry-btn" onClick={() => onEdit(widget)}>Edit widget</button>
            )}
          </div>
        ) : cutShortNoData ? (
          // Nothing survived to show, so the panel takes the card. Retry stays:
          // narrowing the prompt is the real fix, but a re-run is cheap and the
          // limit is not always hit twice.
          <div className="wc-state wc-state-truncated">
            <Icon name="content_cut" />
            <p>{error || 'The answer was cut short before it could be completed.'}</p>
            <button className="wc-retry-btn" onClick={runQuery}>Retry</button>
          </div>
        ) : isDisabled ? (
          <div className="wc-state">
            <p>{error || 'This widget is not enabled.'}</p>
          </div>
        ) : (
          <>
            {Renderer && chartData && size.width > 0 && (
              <Renderer
                data={rendererInput(widget.widget_type_id, chartData)}
                width={size.width}
                height={size.height}
                xLabel={chartData?.x_label || widget.x_label}
                yLabel={chartData?.y_label || widget.y_label}
                title={widget.title}
                displayLabel={widget.display_label}
                labelMode={textEditEnabled}
                meta={liveMeta}
              />
            )}
            {(cutShortWithData || truncated) && (
              // One stack for every caveat note, so whichever apply lay out in a
              // column. Positioned individually they landed on the same spot and
              // overlapped into unreadable text.
              <div className="wc-note-stack">
                {cutShortWithData && (
                  <div className="wc-truncation-note">
                    The answer was cut short — showing the last complete result.
                  </div>
                )}
                {truncated && (
                  <div className="wc-truncation-note">
                    Showing {liveMeta.shown_points} of {liveMeta.total_points} points
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      {/* PRD-1370 #1/#2/#3: footer provenance removed — the range moved to the header,
          the duplicate "Computed" timestamp was dropped (header "Last fetched" is the
          single one), and the dataset name was removed (doesn't scale with many/long
          names). */}
    </div>
  );
}

export default WidgetCard;
